<template>
  <div class="cscscs">
    <BaseModal
      hookSufix="SubtitleModal"
      :disableDefaultPadding="true"
      :onCloseHook="onCloseHook"
      :onOpenHook="onOpenHook"
      :persistent="!screenTitle && !initialScreen"
      :hideCloseBtn="true"
      :minHeight="minHeight"
      :modalWidth="checkoutAi ? '1020' : '690px'"
      :fullscreen="isMobileBreakpoint"
    >
      <template #content>
        <screen-initial-dub-sub-template
          :type="'Subtitle'"
          v-if="initialScreen"
        />
        <upload-dub-sub-template
          :type="'Subtitle'"
          :unusedLangCodes="unusedLangCodes"
          v-if="uploadScreen"
        />
        <screen-title-dub-sub-template
          :type="'Subtitle'"
          v-if="screenTitle"
          :isBatch="isBatch"
        />
        <select-dub-sub-template
          :type="'Subtitle'"
          :options="filteredAiSubtitleOptions"
          :videos="isBatch ? batchSubConfigComputed : [video]"
          :price="generatedSubtitleTotalPrice"
          v-if="selectSubtitles"
          :allLanguages="availableTranscriptionLangCodes"
          :alreadySelected="generateSubtitleData.selectedItems"
        />
        <checkout-ai-template
          :type="'Subtitle'"
          v-if="checkoutAi"
          :videos="isBatch ? batchSubConfigComputed : [video]"
          :totalPrice="generatedSubtitleTotalPrice"
          :itemsToBuy="itemsToBuy"
          :remainingCredits="creditSubRemainCalc"
          :isBatch="isBatch"
          @generateSubtitleBatch="handleGenerateSubtitle"
        />
        <GenerateAiTemplate type="Subtitle" v-if="generatedSubtitle" />
      </template>
    </BaseModal>
  </div>
</template>

<script>
import BaseModal from '@/components/BaseModal.vue'
import i18n from '@/translations/lang'
import videosApi from '@/services/videosApi'
import aiworkflowApi from '@/services/aiworkflowApi'
import aiWorkflowMixin from '@/utils/aiWorkflow.mixin'
import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import formatTimeMixin from '@/utils/formatTime.mixins.js'
import batchAiGenerationSubtitleModal from '@/utils/batchAiGenerationSubtitleModal.mixin.js'
import SelectDubSubTemplate from '../SelectDubSubTemplate.vue'
import CheckoutAiTemplate from '../CheckoutAiTemplate.vue'
import ScreenTitleDubSubTemplate from '../ScreenTitleDubSubTemplate.vue'
import ScreenInitialDubSubTemplate from '../ScreenInitialDubSubTemplate.vue'
import UploadDubSubTemplate from '../UploadDubSubTemplate.vue'
import GenerateAiTemplate from '@/components/GenerateAiTemplate.vue'
export default {
  name: 'AddSubtitleModal',
  mixins: [aiWorkflowMixin, formatTimeMixin, batchAiGenerationSubtitleModal],
  components: {
    BaseModal,
    SelectDubSubTemplate,
    CheckoutAiTemplate,
    ScreenTitleDubSubTemplate,
    ScreenInitialDubSubTemplate,
    UploadDubSubTemplate,
    GenerateAiTemplate,
  },
  data: () => ({
    generateSubtitleData: {
      subtitleSrcLangFromVideo: '',
      subtitleLabelFromAnotherExistingOne: '',
      searchAvailableLangsFilter: '',
      subtitleFromAiOption: 0,
      selectedItems: [],
      context: '',
    },
    videoTime: '',
    uploadScreen: false,
    generateSubtitleDataInitial: {},
    subtitles: [],
    currentModalStage: {
      globalStage: 'initial',
      subStage: 1,
    },
    errors: {},
    errorSituation: false,
    loadings: {
      subtitle: false,
      generateSubtitle: false,
    },
    videoId: '',
    checkoutAi: false,
    selectSubtitles: false,
    generatedSubtitle: false,
    screenTitle: false,
    initialScreen: false,
    itemsToBuy: [],
    subtitleAiPrice: 1, // promotional - was 3
    preSelectedLang: null,
  }),
  computed: {
    ...mapState({
      video: (state) => state.video.video,
      subtitleUploadData: (state) => state.video.subtitleData.upload,
      isMobileBreakpoint: (state) => state.user.isMobileBreakpoint,
    }),
    ...mapState('user', {
      planInfo: 'userPlanInfo',
    }),
    ...mapGetters('user', ['creditsBalance']),
    formatVideoTime() {
      return this.formatTime(this.video.length)
    },
    currentChosenOption() {
      if (this.generateSubtitleData.subtitleFromAiOption === 0)
        return this.generateSubtitleData.subtitleSrcLangFromVideo
      return this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
    },
    batchSubConfigComputed() {
      return this.batchSubConfig
    },
    initialStageCardsData() {
      return [
        {
          title: this.$i18next.t('message.ai.auto'),
          description: this.$i18next.t(
            'message.ai.subtitles.modal.auto.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.subtitles.modal.auto.modalAction'
          ),
          action: this.goToGenerateStage,
          isBeta: true,
          isAi: true,
          imgSource: require('@/assets/robot.svg'),
        },
        {
          title: this.$i18next.t('message.ai.upload'),
          description: this.$i18next.t(
            'message.ai.subtitles.modal.upload.description'
          ),
          actionBtnText: this.$i18next.t(
            'message.ai.subtitles.modal.upload.modalAction'
          ),
          action: this.goToUploadStage,
          isAi: false,
          imgSource: require('@/assets/upload.svg'),
        },
      ]
    },
    isAnyLoadingRunning() {
      return Boolean(
        Object.values(this.loadings).filter((value) => value).length
      )
    },
    selectedItemsToBeUploaded() {
      return this.generateSubtitleData.selectedItems.filter(
        (item) => !this.checkIfListItemIsSource(item)
      )
    },
    isSourceItemSelected() {
      return this.generateSubtitleData.selectedItems.some((item) =>
        this.checkIfListItemIsSource(item)
      )
    },
    generatedSubtitleTotalPrice() {
      if (this.isBatch) {
        return this.batchTotalPrice
      }
      const length = this.video.length
      const minutes = Math.floor(length / 60)
      const seconds = Math.floor(length % 60)
      const subs_to_queue = this.generateSubtitleData.selectedItems.length
      if (!subs_to_queue) return 0
      const credits_per_video = this.applyCreditsCalcRule({ minutes, seconds })
      const final_price = subs_to_queue * credits_per_video

      return final_price
    },
    creditSubRemainCalc() {
      return this.creditsBalance - this.generatedSubtitleTotalPrice
    },
    isGenerateSubtitleBtnDisabled() {
      let condition = true
      if (this.currentModalStage.subStage === 1) {
        if (
          this.generateSubtitleData.subtitleFromAiOption === 0 &&
          !this.generateSubtitleData.subtitleSrcLangFromVideo
        )
          condition = false
        if (
          this.generateSubtitleData.subtitleFromAiOption === 1 &&
          !this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
        )
          condition = false
        if (!this.unusedSubLangCodesAi.length) condition = false
      }
      return this.loadings.generateSubtitle || !condition
    },
    isActionButtonsHighlighted() {
      if (
        this.currentModalStage.globalStage === 'generate' &&
        this.currentModalStage.subStage === 2
      )
        return true
      return false
    },
    disableDefaultPadding() {
      if (this.currentModalStage.globalStage !== 'initial') return true
      return false
    },
    checkboxIndeterminateStatus() {
      return (
        this.generateSubtitleData.selectedItems.length > 0 &&
        this.generateSubtitleData.selectedItems.length <
          this.filteredAiSubtitleOptions.length
      )
    },
    isAnyAiSubtitleItemSelected() {
      return this.generateSubtitleData.selectedItems.length > 0
    },
    allAvailableAiSubtitleOptions() {
      const filteredSubtitleOptions = this.unusedLangCodes.filter(
        ({ label }) => {
          let compareLabel = ''
          if (
            this.generateSubtitleData.subtitleSrcLangFromVideo ||
            this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
          ) {
            compareLabel =
              this.generateSubtitleData.subtitleFromAiOption === 0
                ? this.generateSubtitleData.subtitleSrcLangFromVideo
                : this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
          }
          const isAllowed = label !== compareLabel
          return isAllowed
        }
      )
      return filteredSubtitleOptions
    },
    filteredAiSubtitleOptions() {
      let filteredSubtitleOptions = this.unusedSubLangCodesAi.filter(
        ({ label }) => {
          return this.checkSearch(label)
        }
      )
      if (this.isBatch) {
        filteredSubtitleOptions = this.handleBatchSubtitleOptions()
      }

      return filteredSubtitleOptions.sort((a, b) => {
        if (
          this.checkIfListItemIsSource(a) &&
          !this.checkIfListItemIsSource(b)
        ) {
          return -1
        } else if (
          !this.checkIfListItemIsSource(a) &&
          this.checkIfListItemIsSource(b)
        ) {
          return 1
        }
        return 0
      })
    },
    disabledSendButton() {
      return this.loadings.subtitle || this.hasDuplicatedLangs
    },
    hasDuplicatedLangsTooltip() {
      if (this.hasDuplicatedLangs)
        return this.$i18next.t(
          'message.ai.subtitles.modal.upload.notDuplicatedLangs'
        )
      return false
    },
    hasDuplicatedLangs() {
      const subtitleLabels =
        this.subtitleUploadData.subtitleListToBeUploaded.map(
          (item) => item.label
        )
      return this.containsDuplicates(subtitleLabels)
    },
    unusedLangCodes() {
      return this.getUnusedSubtitleItems(this.langCodes)
    },
    addSubtitleFileErrorMsg() {
      if (this.errors.wrongSubtitleFile) {
        return this.$i18next.t(
          'message.pages.videoEditModal.errors.wrongSubtitleFile'
        )
      }
      return ''
    },
    logoConfig() {
      return {
        top: -83,
        left: 295,
        size: 0.5,
        rotation: 0,
      }
    },
    arrowConfig() {
      return {
        top: -50,
        left: 100,
        size: 0.65,
        rotation: 100,
      }
    },
    textConfig() {
      return {
        text: this.$i18next.t('message.ai.aiWorkFlowChargeModal.emptyState'),
        top: 70,
        left: 235,
        size: '1em',
        rotation: 0,
        textAlign: 'center',
        textWidth: '40%',
        hrefText: this.$i18next.t(
          'message.ai.aiWorkFlowChargeModal.addCredits'
        ),
        onClick: this.handleGoChargeCredits,
      }
    },
    minHeight() {
      return this.isMobileBreakpoint ? '100%' : 'auto'
    },
    hasEnoughCredits() {
      const subPrice = this.generatedSubtitleTotalPrice
      const hasEnoughCredits = this.calcEnoughCredits(subPrice)
      return !(hasEnoughCredits < 0)
    },
    isStepTwo() {
      return this.currentModalStage.subStage === 2
    },
    isStepThree() {
      return this.currentModalStage.subStage === 3
    },
    hasAiSubtitle() {
      return this.generateSubtitleData.subtitleFromAiOption === 1
    },
    isGlobalStateUpload() {
      return this.currentModalStage.globalStage === 'upload'
    },
    nounNumber() {
      if (this.batchAllVideosCount > 1) return 'videos'
      return 'video'
    },
  },
  watch: {
    open(val) {
      if (!val) this.handleClose()
    },
    subtitles() {
      this.checkSubtitleOption()
    },
  },
  methods: {
    ...mapMutations('video', [
      'UPDATE_VIDEO',
      'UPDATE_GENERATING_SUBTITLE_DATA',
      'UPDATE_VIDEO_SUBTITLES',
    ]),
    ...mapActions('user', ['updateCreditsBalance']),
    getItemCost(item) {
      let videoTime = Math.floor(this.video.length / 60)
      if (!videoTime) videoTime = 1
      const calc = this.getMultiplierAmount(item) * videoTime
      return calc ? calc : 1
    },
    updateSubStage(subStage) {
      this.currentModalStage.subStage = subStage
    },
    getMultiplierAmount(item) {
      if (this.sourceItem.srclang !== 'auto') {
        if (this.checkIfListItemIsSource(item)) {
          return 3 + 1
        }
        return 1
      }
      if (
        this.generateSubtitleData.selectedItems.length &&
        this.generateSubtitleData.selectedItems[0].srclang === item.srclang
      ) {
        return 1 + 3
      }
      return 1
    },
    canShowRowValues(item) {
      let isSourceItemSelected = this.isSourceItemSelected
      if (this.sourceItem.srclang === 'auto') {
        isSourceItemSelected = Boolean(
          this.generateSubtitleData.selectedItems.length
        )
      }

      return (
        (this.checkIfListItemIsSource(item) && isSourceItemSelected) ||
        (!this.checkIfListItemIsSource(item) &&
          isSourceItemSelected &&
          this.checkIfItemIsSelected(item))
      )
    },
    checkIfItemIsSelected({ srclang }) {
      return this.generateSubtitleData.selectedItems.some(
        (item) => item.srclang === srclang
      )
    },
    hideCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton && this.isMobileBreakpoint) {
        crispButton.style.setProperty('display', 'none', 'important')
      }
    },
    showCrispButton() {
      const crispButton = document.querySelector('.crisp-client')
      if (crispButton) {
        crispButton.style.removeProperty('display')
      }
    },
    checkSubtitleOption() {
      if (this.subtitles?.length) {
        this.generateSubtitleData.subtitleFromAiOption = 1
      } else {
        this.generateSubtitleData.subtitleFromAiOption = 0
      }
    },
    onOpenHook({ videoId, initialState, isBatch, selectedLang }) {
      this.hideCrispButton()
      this.$root.$on('selectedSubtitleItem', (data) => {
        this.generateSubtitleData.selectedItems = data
        this.itemsToBuy = data.map((row) => row.label)
      })
      this.$root.$on('checkoutSubtitleModal', () => {
        this.checkoutAi = true
        this.selectSubtitles = false
      })
      this.$root.$on('backSubtitleModal', () => {
        this.checkoutAi = false
        this.selectSubtitles = true
      })
      if (selectedLang) {
        this.generateSubtitleData.selectedItems =
          this.filteredAiSubtitleOptions.filter(
            (v) => selectedLang.srclang == v.srclang
          )
        this.itemsToBuy = this.generateSubtitleData.selectedItems.map(
          (row) => row.label
        )
      } else {
        this.generateSubtitleData.selectedItems = []
        this.itemsToBuy = []
      }
      this.$root.$on('defaultSubtitleLanguage', (data) => {
        this.generateSubtitleData.subtitleSrcLangFromVideo = data
      })
      this.$root.$on('screenTitleSubtitle', () => {
        this.activateScreen(this, 'screenTitle')
      })
      this.$root.$on('selectSubtitleScreen', () => {
        this.activateScreen(this, 'selectSubtitles')
      })
      this.$root.$on('uploadSubtitleScreen', () => {
        this.activateScreen(this, 'uploadScreen')
      })
      this.$root.$on('backSubtitleInitialScreen', () => {
        this.activateScreen(this, 'initialScreen')
      })
      // this.updateCreditsBalance()
      if (isBatch) {
        this.$root.$on('generateSubtitleBatch', async () => {
          await this.handleBatchSubGeneration()
        })
        this.activateScreen(this, 'screenTitle')
        this.isBatch = isBatch
        this.handleOpenBatchSubGenerationModal()
        return
      } else {
        this.$root.$on('generateSubtitle', async () => {
          await this.handlePostAiGeneratedSubtitle()
        })
        this.isBatch = false
      }
      this.generateSubtitleDataInitial = _.cloneDeep(this.generateSubtitleData)
      this.videoId = videoId
      this.open = true
      this.subtitles = this.$store.state.video.video.config?.subtitles || []
      if (initialState) this.activateScreen(this, 'selectSubtitles')
      else this.activateScreen(this, 'initialScreen')
      if (this.video.original_lang) {
        this.generateSubtitleData.subtitleSrcLangFromVideo =
          this.video.original_lang
      } else if (this.planInfo.currency) {
        if (this.planInfo.currency === 'brl') {
          this.generateSubtitleData.subtitleSrcLangFromVideo = 'pt-BR'
        } else {
          this.generateSubtitleData.subtitleSrcLangFromVideo = 'en'
        }
      } else {
        this.generateSubtitleData.subtitleSrcLangFromVideo = 'auto'
      }

      if (this.subtitles?.length) {
        this.generateSubtitleData.subtitleLabelFromAnotherExistingOne =
          this.subtitles[0].srclang
      }
      if (initialState) {
        this.handleUpdateModalStage({
          globalStage: 'generate',
          subStage: 1,
        })
      }
      if (selectedLang) {
        this.preSelectedLang = selectedLang
      }
      this.checkSubtitleOption()
    },
    async handleGenerateSubtitle() {
      await this.handleBatchSubGeneration()
    },
    activateScreen(componentInstance, screenName) {
      const screens = [
        'selectSubtitles',
        'uploadScreen',
        'checkoutAi',
        'initialScreen',
        'screenTitle',
        'generatedSubtitle',
      ]
      screens.forEach((screen) => {
        componentInstance[screen] = screen === screenName
      })
    },
    selectAllItems() {
      let arr = []

      if (
        this.generateSubtitleData.selectedItems.length !==
        this.filteredAiSubtitleOptions.length
      ) {
        arr = this.filteredAiSubtitleOptions
      }

      this.generateSubtitleData.selectedItems = arr
    },
    checkboxSelection(item) {
      let alreadySelected = this.checkIfSelected(item)
      if (alreadySelected) {
        let newArr = [
          ...this.generateSubtitleData.selectedItems.filter((lookItem) => {
            return lookItem.srclang !== item.srclang
          }),
        ]
        this.generateSubtitleData.selectedItems = [...newArr]
      } else {
        this.generateSubtitleData.selectedItems.push(item)
      }
    },
    checkIfSelected(item) {
      let foundItem = this.generateSubtitleData.selectedItems.find(
        (searchedItem) => {
          return searchedItem.srclang === item.srclang
        }
      )
      if (foundItem) return true
      return false
    },
    handlePreSelectedLang() {
      const { subtitleSrcLangFromVideo } = this.generateSubtitleData
      const { srclang } = this.preSelectedLang
      if (subtitleSrcLangFromVideo !== srclang) {
        this.checkIfSelected(this.preSelectedLang)
        this.checkboxSelection(this.preSelectedLang)
      }
    },
    checkSearch(label) {
      if (this.generateSubtitleData.searchAvailableLangsFilter) {
        return this.areStringsEqualIgnoringCaseAndSymbols(
          this.generateSubtitleData.searchAvailableLangsFilter,
          label
        )
      }
      return true
    },
    getLangCodeByLabel(incomingLabel) {
      return this.langCodes.filter(({ label }) => label === incomingLabel)[0]
    },
    async handlePostAiGeneratedSubtitle() {
      this.loadings.generateSubtitle = true
      let data = {}
      try {
        if (!this.generateSubtitleData.subtitleFromAiOption) {
          const additionalLangs = this.generateSubtitleData.selectedItems.map(
            (item) => ({
              lang: this.getPreparedSrcLang(item.srclang),
            })
          )
          data = await aiworkflowApi.createTranscription({
            fromLang: this.getPreparedSrcLang(
              this.generateSubtitleData.subtitleSrcLangFromVideo
            ),
            videoId: this.videoId,
            additionalLangs,
            ctx: this.generateSubtitleData.context,
          })
          const batchMetadata = data.data
          this.$root.$emit('batchGenerationMetadata', batchMetadata)

          const dataToUpdate = {
            video_id: data.data.video_id,
            subtitles: [
              this.getPreparedSrcLang(
                this.generateSubtitleData.subtitleSrcLangFromVideo
              ),
              ...additionalLangs.map((lang) => ({
                srclang: lang.lang,
                translation: true,
              })),
            ],
          }

          this.handleVideosIdAiWorkflow(this.videoId)

          this.updateDbSubtitles(dataToUpdate.video_id, dataToUpdate.subtitles)

          if (
            this.generateSubtitleData.subtitleSrcLangFromVideo &&
            this.generateSubtitleData.subtitleSrcLangFromVideo !== 'auto'
          ) {
            this.UPDATE_VIDEO_ORIGINAL_LANG(
              this.generateSubtitleData.subtitleSrcLangFromVideo
            )
            this.$root.$emit('updateVideoInfo', this.video)
          }
          this.$root.$emit('addAiWorkflowSubtitle', data.data)
        } else {
          const translationRequests = this.selectedItemsToBeUploaded.map(
            (item) => ({
              toLang: item.srclang,
            })
          )

          const fromLangValue =
            typeof this.generateSubtitleData
              .subtitleLabelFromAnotherExistingOne === 'object'
              ? this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
                  .srclang
              : this.generateSubtitleData.subtitleLabelFromAnotherExistingOne
          const response = await aiworkflowApi.createTranslation({
            fromLang: this.getPreparedSrcLang(fromLangValue),
            videoId: this.videoId,
            toLangs: translationRequests,
          })

          this.handleVideosIdAiWorkflow(this.videoId)
          if (!Array.isArray(response.data)) response.data = [response.data]
          const _this = this
          this.processSubtitles(response.data)
          this.addSubtitlesToBatchQueueDb(response.data)
          response.data.map((item) => {
            this.$nextTick(() => {
              _this.$root.$emit('addAiWorkflowSubtitle', item)
              _this.$root.$emit('batchGenerationMetadata', item)
            })
          })
        }
        this.activateScreen(this, 'generatedSubtitle')
        this.clearErrors()
        this.currentModalStage.subStage += 1
      } catch (err) {
        if (this.isBatch) {
          this.currentModalStage.subStage = 3
        } else {
          console.error(err)
          console.error({ ...err })
          if (
            err?.response?.data &&
            err?.response?.data?.errMsg &&
            err?.response?.data?.errCode
          ) {
            this.errorSituation = true
            console.error(err.response.data.errMsg)
            const badRequestAllowedValues =
              'should be equal to one of the allowed values: en, en-US, pt, auto, pt-BR, es, es-MX, en-UK, it, it-ch, fr, fr-be, fr-ca, fr-lu, fr-ch, de, de-at, de-li, de-lu, de-ch.'
            this.errors.generate = `${err.response.data.errCode}: ${err.response.data.errMsg}`
            if (err.response.data.errMsg.includes(badRequestAllowedValues)) {
              this.errors.generate = `${i18n.t(
                'message.ai.noLanguageSupport'
              )}: ${i18n.t('message.ai.skippedWarningNotSupported')}`
            }
          } else if (err?.message) {
            console.error(err.message)
            this.errors.generate = err.message
          }
        }
      }
      this.loadings.generateSubtitle = false
      // this.$root.$emit('reloadBalance')
    },
    async addSubtitlesToBatchQueueDb(data) {
      try {
        const subtitlesToBeGeneratedFiltered = data.filter(
          (item) =>
            !this.subtitleGeneratingData.some((sub) => {
              if (sub.id !== item.id) {
                return {
                  srclang: item.to_lang || item.metadata?.to_lang,
                  translation: true,
                }
              }
            })
        )

        this.UPDATE_GENERATING_SUBTITLE_DATA(subtitlesToBeGeneratedFiltered)
      } catch (error) {
        console.error('Error adding subtitles to batch queue db:', error)
      }
    },
    processSubtitles(data) {
      const groupedItems = data.reduce((acc, item) => {
        if (!acc[item.video_id]) {
          acc[item.video_id] = []
        }
        acc[item.video_id].push({
          srclang: item.to_lang || item?.metadata?.to_lang,
          translation: true,
        })
        return acc
      }, {})

      Object.entries(groupedItems).forEach(([video_id, subtitles]) => {
        this.updateDbSubtitles(video_id, subtitles)
      })
    },
    async updateDbSubtitles(video_id, subtitles) {
      try {
        const videoConfig = await window.db.videos
          .where({ id: video_id })
          .first()
        if (!videoConfig) {
          console.error(`Video with ID ${video_id} not found.`)
          return
        }

        videoConfig.subtitles = [
          ...new Set([...(videoConfig.subtitles || []), ...subtitles]),
        ]

        await window.db.videos.update(video_id, videoConfig)
      } catch (error) {
        console.error(
          `Error updating subtitles for video ID ${video_id}:`,
          error
        )
      }
    },
    async handlePostNewSubtitles() {
      this.loadings.subtitle = true
      for (let item of this.subtitleUploadData.subtitleListToBeUploaded) {
        await this.handleAddSubtitle(item)
      }
      this.loadings.subtitle = false
      this.handleClose()
      this.subtitleUploadData.subtitleListToBeUploaded = []
    },
    async handleAddSubtitle(subtitle) {
      this.clearErrors()
      const data = new FormData()
      const langCode = this.langCodes.filter((lang) => {
        return lang.label === subtitle.label
      })
      subtitle.srclang = langCode[0].srclang
      data.append('file', subtitle.file)
      data.append('label', subtitle.label)
      data.append('srclang', subtitle.srclang)
      try {
        await videosApi.addSubtitleToVideo(this.videoId, data)
        this.loadings.subtitle = false
        const subtitles = this.$store.state.video.video.config?.subtitles || []
        subtitles.push({
          label: subtitle.label,
          srclang: subtitle.srclang,
        })

        const videoConfig = await window.db.videos
          .where({ id: this.videoId })
          .first()

        videoConfig.subtitles = subtitles

        await window.db.videos.update(this.videoId, videoConfig)

        this.UPDATE_VIDEO_SUBTITLES(subtitles)
        this.$root.$emit('updateVideoSubtitles', subtitles)
      } catch (e) {
        console.error(e)
      }
    },
    clearErrors() {
      Object.keys(this.errors).forEach((e) => (this.errors[e] = false))
    },
    handleClose() {
      this.activateScreen(this, 'initialScreen')
      this.screenTitle = false
      this.$root.$emit('closeSubtitleModal')
    },
    onCloseHook() {
      this.showCrispButton()
      this.$root.$off('generateSubtitle')
      this.$root.$off('screenTitleSubtitle')
      this.generateSubtitleData = _.cloneDeep(this.generateSubtitleDataInitial)
      this.preSelectedLang = null
      this.clearErrors()
      this.goToInitial()
    },
    goToGenerateStage() {
      this.handleUpdateModalStage({
        globalStage: 'generate',
        subStage: 1,
      })
    },
    goToUploadStage() {
      this.handleUpdateModalStage({
        globalStage: 'upload',
        subStage: 1,
      })
    },
    goToInitial() {
      this.handleUpdateModalStage()
    },
    handleUpdateModalStage({ globalStage = 'initial', subStage = 1 } = {}) {
      this.currentModalStage.globalStage = globalStage
      this.currentModalStage.subStage = subStage
    },
    handleGoChargeCredits() {
      this.handleClose()
      return this.$router.push({ name: 'Credits', query: { charge: 'true' } })
    },
    calcEnoughCredits(price) {
      return this.creditsBalance - price
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_ai_works';
</style>

<style lang="scss" scoped>
.v-simple-checkbox {
  i {
    color: var(--v-primary-base) !important;
  }
}
.text-bold {
  font-weight: 500;
  font-size: 16px;
}
.container {
  &__setup-action {
    width: 347px;
    margin: auto;
  }
}
.container__credit-spend {
  width: 400px;
  position: relative;
  left: 0;
  margin: auto;
}
.bg-color {
  background-color: var(--v-newBackground-base) !important;
}
.card-height {
  height: auto !important;
}
.hide-card {
  visibility: hidden;
}
.card-height-step3 {
  max-height: 30px;
}
.btn-width {
  width: 100px;
}
.margin-stp3 {
  margin-top: 11px !important;
}
</style>

<style lang="scss">
.bg-color-icon {
  .v-image__image {
    background-color: var(--v-newBackground-base) !important;
  }
}

@media screen and (max-height: 768px) {
  .v-card__actions--bot-position {
    height: 100%;
    margin-bottom: -3%;
  }

  .credit-spend-col {
    display: flex;
    align-items: center;
  }
}
</style>
